import Vue from 'vue';
import store from './store';
import * as utils from '../utils/request';
import _ from 'lodash';

/**
 * @typedef {Object} Benefactor
 * @property {string} uid
 * @property {string} picURL
 * @property {Object} name
 * @property {string} name.firstName
 * @property {string} name.lastName
 */

/**
 * @typedef {Object} Beneficiary
 * @property {string} id
 * @property {string} picURL
 * @property {Object} name
 * @property {string} name.firstName
 * @property {string} name.lastName
 * @property {string} ambition
 * @property {string} storyShort
 * @property {boolean} published
 * @property {Funding} activeFundingObject
 * @property {Benefactor[]} benefactors[]
 * @property {Donation[]} donations
 * @property {Organization} organization
 */

store.registerModule('beneficiaries', {
  namespaced: true,
  state: {
    /** @type {Beneficiary[]} */
    beneficiaries: []
  },
  mutations: {
    /**
     * @param state
     * @param {Object} opts
     * @param {Beneficiary[]} opts.items
     * @param {boolean} opts.upsert
     * @param {boolean} opts.remove
     */
    updateInBeneficiaries (state, opts) {
      const { items, upsert, remove } = opts;
      const list = state.beneficiaries;
      items.forEach(item => {
        const index = list.findIndex(b => b.id === item.id);
        if (~index) {
          Vue.set(list, index, item);
          return;
        }

        if (upsert) {
          list.push(item);
        }

        if (remove) {
          _.remove(list, (i) => i.id === item.id);
        }
      });
    }
  },
  actions: {
    /**
     * @param ctx
     * @param {Object} opts
     * @param {number} opts.limit
     * @param {number} opts.skip
     * @param {boolean} opts.random
     * @returns {Beneficiary[]}
     */
    async getList (ctx, opts) {
      opts = Object.assign({ limit: 50 }, opts);
      const items = await utils.request('beneficiaries', {
        method: 'get',
        searchParams: {
          // $limit: opts.limit,
          organization: opts.organization
        }
      });
      ctx.commit('updateInBeneficiaries', {
        items: items,
        upsert: true
      });
      return items;
    },

    /**
     * @param ctx
     * @param {Object} opts
     * @param {boolean} opts.published
     * @param {string} opts.keyword
     * @returns {Beneficiary[]}
     */
    getListByPublishStatus (ctx, opts) {
      opts = Object.assign({}, opts);
      if (typeof opts.published === 'undefined') throw new Error(`Published is required`);

      const filtered = ctx.state.beneficiaries.filter(b => {
        const publisherResult = !!b.published === opts.published;
        const keywordResult = typeof opts.keyword === 'undefined' ||
          (b.name.firstName.toUpperCase().search(opts.keyword.toUpperCase()) > -1 || b.name.lastName.toUpperCase().search(opts.keyword.toUpperCase()) > -1);

        return publisherResult && keywordResult;
      });

      return filtered;
    },

    /**
     * @param ctx
     * @param {Object} opts
     * @param {string} opts.id
     * @param {boolean} opts.published
     * @param {boolean} opts.$forceFetch
     * @returns {Beneficiary}
     */
    async getById (ctx, opts) {
      opts = Object.assign({}, opts);
      if (!opts.id) throw new Error(`Id is required`);

      // check cache
      const cache = ctx.state.beneficiaries.find(b => b.id === opts.id);
      const complete = cache && cache.benefactors && cache.donations;
      if (complete && !opts.$forceFetch) return cache;

      // fetch item/get from cache
      const item = cache || await utils.request(`beneficiaries/${opts.id}`, {
        method: 'get'
      });

      ctx.commit('updateInBeneficiaries', {
        items: [ item ],
        upsert: true
      });
      return item;
    },

    /**
     * @param ctx
     * @param {Object} opts
     * @param {string} opts.firstName
     * @param {string} opts.lastName
     * @param {string} opts.ambition
     * @param {string} opts.storyShort
     * @param {string} opts.storyLong
     * @param {string} opts.organizationId
     * @param {Object} opts.timeline
     */
    async create (ctx, opts) {
      const body = {
        name: {
          firstName: opts.firstName,
          lastName: opts.lastName
        },
        ambition: opts.ambition,
        storyShort: opts.storyShort,
        storyLong: opts.storyLong,
        organization: opts.organizationId
      };

      const item = await utils.request(
        `beneficiaries`,
        {
          method: 'post',
          body
        }
      );

      store.dispatch(
        'timeline/create',
        {
          beneficiary: item.id,
          title: opts.timeline.title,
          body: opts.timeline.body
        }
      );

      ctx.commit('updateInBeneficiaries', {
        items: [ item ],
        upsert: true
      });

      return item;
    },

    /**
     * @param ctx
     * @param {Object} opts
     * @param {string} opts.id
     * @param {string} opts.name
     * @param {string} opts.ambition
     * @param {string} opts.storyShort
     * @param {string} opts.storyLong
     * @param {string} opts.activeFunding
     * @param {string} opts.picURL
     * @param {boolean} opts.$publish
     */
    async update (ctx, opts) {
      const body = { ..._.omit(opts, 'id') };

      const item = await utils.request(
        `beneficiaries/${opts.id}`,
        {
          method: 'put',
          body
        }
      );

      ctx.commit(
        'updateInBeneficiaries',
        {
          items: [ item ],
          update: true
        }
      );

      return item;
    },

    /**
     * @param ctx
     * @param {Object} opts
     * @param {string} opts.id
     */
    async delete (ctx, opts) {
      const item = await utils.request(
        `beneficiaries/${opts.id}`,
        {
          method: 'delete'
        }
      );

      ctx.commit(
        'updateInBeneficiaries',
        {
          items: [ item ],
          remove: true
        }
      );
    }
  }
});
